// hooks/billing/useTransactions.js
import { useCallback, useEffect, useRef, useState } from 'react';
import BillingService from '../../services/billing.service';

/**
 * useTransactions — `GET /accounts/{id}/billing/transactions` with
 * cursor-based pagination.
 *
 * Filter changes (`accountId`, `resourceTypes`, `transactionType`,
 * `since`, `until`, `limit`) reset the cursor and reload the first
 * page. `loadMore()` advances using the last `nextCursor` returned by
 * the backend. `items` accumulates pages so consumers can render a
 * single growing list.
 *
 * @param {string} accountId
 * @param {Object} [params]
 * @param {number} [params.limit=20]
 * @param {string[]} [params.resourceTypes]
 * @param {string} [params.transactionType]   DEDUCTION | RECHARGE | REFUND | BONUS
 * @param {string} [params.since]             ISO 8601
 * @param {string} [params.until]             ISO 8601
 * @param {boolean} [params.auto=true]
 *
 * @returns {{
 *   items: Array,
 *   loading: boolean,
 *   loadingMore: boolean,
 *   hasMore: boolean,
 *   error: Object|null,
 *   refetch: () => Promise<void>,
 *   loadMore: () => Promise<void>,
 * }}
 */
export function useTransactions(
  accountId,
  {
    limit = 20,
    resourceTypes,
    transactionType,
    since,
    until,
    auto = true,
    // Los eventos de importe cero se descartan **en la consulta**, no
    // después: el backend calcula `hasMore` y `nextCursor` sobre el conjunto
    // ya filtrado. Filtrar lo que ya llegó dejaba páginas enteras vacías —
    // en este libro mayor nueve de cada diez filas valen cero— y el
    // paginador ofrecía la siguiente igual.
    excludeZeroAmount = false,
  } = {}
) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(Boolean(auto && accountId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Cursor lives in a ref so loadMore reads the latest one without
  // re-creating the callback on every page.
  const cursorRef = useRef(null);

  // Paginación por cursor: la pila guarda con qué cursor se pidió cada
  // página, que es lo único que permite volver atrás. El backend no expone
  // desplazamiento ni número de página —solo `nextCursor`—, así que
  // "saltar a la 9" no es algo que se pueda ofrecer sin mentir.
  const [pageIndex, setPageIndex] = useState(0);
  const cursorStack = useRef([null]);

  const fetchPage = useCallback(
    async ({ append, cursor, reset }) => {
      if (!accountId) return;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          cursorRef.current = null;
          cursorStack.current = [null];
          setPageIndex(0);
        }
      }
      setError(null);
      const result = await BillingService.listTransactions(accountId, {
        cursor: append ? cursorRef.current : (cursor ?? undefined),
        limit,
        resourceTypes,
        transactionType,
        since,
        until,
        excludeZeroAmount,
      });
      if (result.success) {
        const page = result.data ?? {};
        const newItems = page.items ?? [];
        setItems((prev) => (append ? [...prev, ...newItems] : newItems));
        cursorRef.current = page.nextCursor ?? null;
        setHasMore(Boolean(page.hasMore));
      } else {
        setError(result);
        // BAD_REQUEST is almost always a malformed cursor — the
        // backend rejects it and any subsequent loadMore would loop
        // on the same bad value. Drop the cursor and lock pagination
        // until the caller resets filters.
        if (result.code === 'BAD_REQUEST') {
          cursorRef.current = null;
          setHasMore(false);
        } else if (!append) {
          setItems([]);
          setHasMore(false);
        }
      }
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    },
    // resourceTypes is an array; rely on referential equality from the
    // caller. Use JSON.stringify if callers pass fresh arrays per render.
    [accountId, limit, resourceTypes, transactionType, since, until, excludeZeroAmount]
  );

  const refetch = useCallback(
    () => fetchPage({ append: false, reset: true }),
    [fetchPage]
  );

  // `loadMore` acumula páginas — sigue ahí para quien lea el libro mayor
  // como una lista que crece, como el cajón de transacciones de una cuenta.
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !cursorRef.current) return;
    await fetchPage({ append: true });
  }, [fetchPage, hasMore, loadingMore]);

  // `nextPage` / `prevPage` **reemplazan**: una página a la vez, que es lo
  // que hace que el paginador diga la verdad. Mezclar las dos cosas —
  // acumular en el hook y rebanar en la tabla— era lo que producía una lista
  // de números de página que solo llegaba hasta donde alguien ya había
  // bajado, con puntos suspensivos prometiendo lo que no se podía alcanzar.
  const nextPage = useCallback(async () => {
    if (!hasMore || loading || !cursorRef.current) return;
    const cursor = cursorRef.current;
    cursorStack.current = [
      ...cursorStack.current.slice(0, pageIndex + 1),
      cursor,
    ];
    setPageIndex(pageIndex + 1);
    await fetchPage({ append: false, cursor });
  }, [fetchPage, hasMore, loading, pageIndex]);

  const prevPage = useCallback(async () => {
    if (pageIndex === 0 || loading) return;
    const cursor = cursorStack.current[pageIndex - 1] ?? undefined;
    setPageIndex(pageIndex - 1);
    await fetchPage({ append: false, cursor });
  }, [fetchPage, loading, pageIndex]);

  useEffect(() => {
    if (auto && accountId) {
      fetchPage({ append: false, reset: true });
    } else if (!accountId) {
      setItems([]);
      setHasMore(false);
      setError(null);
      setLoading(false);
      cursorRef.current = null;
      cursorStack.current = [null];
      setPageIndex(0);
    }
  }, [auto, accountId, fetchPage]);

  return {
    items,
    loading,
    loadingMore,
    hasMore,
    error,
    refetch,
    loadMore,
    page: pageIndex,
    hasPrevPage: pageIndex > 0,
    hasNextPage: hasMore,
    nextPage,
    prevPage,
  };
}

export default useTransactions;
